import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { getAllProperties } from '@/lib/api/api'
import { useAuth } from './AuthContext'

interface Site {
  propertyId: string
  name: string
  domain: string
  subscription?: {
    status?: string
    isFreePlan?: boolean
    localTrialActive?: boolean
    planSource?: string
  }
}

interface SiteContextType {
  id: string
  setId: (id: string) => void
  currentSite: Site | null
  setCurrentSite: (site: Site | null) => void
  sites: Site[]
  setSites: (sites: Site[]) => void
  isLoadingSites: boolean
  sitesLoadError: boolean
}

const SiteContext = createContext<SiteContextType | null>(null)

export function SiteProvider({ children }: { children: ReactNode }) {
  const [id, setId] = useState('')
  const [currentSite, setCurrentSite] = useState<Site | null>(null)
  const [sites, setSites] = useState<Site[]>([])
  const [isLoadingSites, setIsLoadingSites] = useState(false)
  const [sitesLoadError, setSitesLoadError] = useState(false)
  const { token } = useAuth()

  // Load sites when token is available
  useEffect(() => {
    const loadSites = async () => {
      if (!token) {
        setSites([])
        setCurrentSite(null)
        setIsLoadingSites(false)
        setSitesLoadError(false)
        return
      }

      setIsLoadingSites(true)
      setSitesLoadError(false)
      try {
        const response = await getAllProperties(token)
        const sitesList = response.properties || []
        const uniqueSites = sitesList.filter((site: Site, index: number, self: Site[]) =>
          index === self.findIndex((s: Site) => s.propertyId === site.propertyId)
        )
        setSites(uniqueSites)

        if (uniqueSites.length > 0 && !currentSite) {
          setCurrentSite(uniqueSites[0])
        }
      } catch (error) {
        console.error('Error loading sites:', error)
        setSitesLoadError(true)
        setSites([])
      } finally {
        setIsLoadingSites(false)
      }
    }

    loadSites()
  }, [token]) // Removed currentSite from dependencies to prevent infinite loop

  return (
    <SiteContext.Provider value={{
      id,
      setId,
      currentSite,
      setCurrentSite,
      sites,
      setSites,
      isLoadingSites,
      sitesLoadError,
    }}>
      {children}
    </SiteContext.Provider>
  )
}

export const useSite = () => {
  const context = useContext(SiteContext)
  if (!context) {
    throw new Error('useSite must be used within SiteProvider')
  }
  return context
}

// Safe hook that returns null if provider is not available
// Use this when the component might be used in workspace mode (without SiteProvider)
export const useSiteSafe = () => {
  return useContext(SiteContext)
}